import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AByte} from '@mintdata/mint-kit/api/values/AByte';
import {ACurrency} from '@mintdata/mint-kit/api/values/ACurrency';
import {ADate} from '@mintdata/mint-kit/api/values/ADate';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AIntegerEnum} from '@mintdata/mint-kit/api/values/AIntegerEnum';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AStringEnum} from '@mintdata/mint-kit/api/values/AStringEnum';
import {ATime} from '@mintdata/mint-kit/api/values/ATime';
import {FrameCellCoordinate, FramePath, IFrame, IFrameRecord} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import * as jp from 'jsonpath/jsonpath.min.js';
import _chunk from 'lodash/chunk';

export const FORCE_LIST_INDICATOR_FIELD_NAME = '__force_list';
export const FORCE_NO_PLUCK_INDICATOR_FIELD_NAME = '__force_no_pluck';
export const JSON_ITEM_FIELD_NAME = '__item';

const SKIP_FIELD_NAMES_IN_JSON = [FORCE_LIST_INDICATOR_FIELD_NAME, FORCE_NO_PLUCK_INDICATOR_FIELD_NAME];

export class JsonUtil
{
    public static convertFrameToJson(frame:IFrame):string
    {
        return JSON.stringify(this.convertFrameToJsonObject(frame));
    }

    public static convertIValueToJsonObject(value:IValue, isForceList:boolean = false):any
    {
        if( value === undefined || value === null )
            return null;

        const type = value.getType();
        switch( true )
        {
            case type.isEqualTo(Type.Boolean):
                return (value as ABoolean).value;
            case type.isEqualTo(Type.Byte):
                return (value as AByte).value;
            case type.isEqualTo(Type.Integer):
                return (value as AInteger).value;
            case type.isEqualTo(Type.Number):
                return (value as ANumber).value;
            case type.isEqualTo(Type.String):
                return (value as AString).value;
            case type.isEqualTo(Type.Currency):
                return (value as ACurrency).value;
            case type.isEqualTo(Type.Date):
                return (value as ADate).toJSDate().getTime();
            case type.isEqualTo(Type.DateTime):
                return (value as ADateTime).toJSDate().getTime();
            case type.isEqualTo(Type.Time):
                return (value as ATime).toString();
            case type.isEqualTo(Type.StringEnum):
                return this.convertIValueToJsonObject((value as AStringEnum).value);
            case type.isEqualTo(Type.IntegerEnum):
                return this.convertIValueToJsonObject((value as AIntegerEnum).value);
            case type.isEqualTo(Type.BinaryData):
                //this is unsupported for now => just return this string
                return '#DATA';
            case type.isEqualTo(Type.Frame):
                return this.convertFrameToJsonObject(value as IFrame, isForceList);
            case type.isEqualTo(Type.Empty):
                return null;
            case type.isEqualTo(Type.Error):
                return (value as AError).error;
        }
    }

    public static convertFrameToJsonObject(frame:IFrame, isForceList:boolean = false):any
    {
        if( !frame )
            return null;

        const fields:Array<FrameField> = frame.getFields()
            .filter(field => SKIP_FIELD_NAMES_IN_JSON.indexOf(field.name) === -1);
        const records:Array<IFrameRecord> = frame.getRecords();

        const unwrapObject = records.length === 1 && !isForceList && !frame.findField(FORCE_LIST_INDICATOR_FIELD_NAME);
        const pluckList = fields.length === 1 && fields[0].name === JSON_ITEM_FIELD_NAME && !frame.findField(FORCE_NO_PLUCK_INDICATOR_FIELD_NAME);

        const result = records.map((record) =>
        {
            const rowObject = {};
            fields.forEach(field =>
            {
                if( SKIP_FIELD_NAMES_IN_JSON.indexOf(field.name) === -1 )
                    rowObject[field.name] = this.convertIValueToJsonObject(record.getCell(field.name), isForceList);
            });
            return rowObject;
        });

        if( pluckList )
            return result.map(rowObject => rowObject[fields[0].name]);
        else if( unwrapObject )
            return result[0];
        else
            return result;
    }

    public static convertJsonValueToIValue(jsonValue:any):IValue
    {
        if( typeof jsonValue === 'boolean' )
            return ABoolean.fromBoolean(jsonValue);
        else if( jsonValue == null )
            return AEmpty.Value;
        else if( typeof jsonValue === 'string' )
            return AString.fromString(jsonValue);
        else if( typeof jsonValue === 'number' )
            return ANumber.fromNumber(jsonValue);
        else if( typeof jsonValue === 'function' )
            return AError.fromString('#INVALID_VALUE');
        else if( typeof jsonValue === 'object' )
            return this.convertJsonObjectToFrame(jsonValue);

        return null;
    }

    public static convertJsonObjectToFrame(jsonObject:any):IFrame
    {
        if( jsonObject === undefined || jsonObject == null )
            return Frames.empty();

        const isArray = Array.isArray(jsonObject);
        const jsonArray = isArray ? jsonObject : [jsonObject];

        // if some of the elements of an array aren't a normal object => convert to "_item"-frame
        const toItemsFrame = jsonArray.some(e => e == null || Array.isArray(e) || typeof e !== 'object');
        if( toItemsFrame )
        {
            const fields = [
                new FrameField(JSON_ITEM_FIELD_NAME, Type.Frame)
            ];
            const data = jsonArray.map(e => [this.convertJsonValueToIValue(e)]);

            return Frames.fromFieldsAndData(fields, data);
        }

        const fieldsNamesToIndexMap = {};
        const fieldsNames:Array<string> = [];
        const fieldsTypes:Array<Type> = [];
        const data:Array<Array<IValue>> = [];
        for( let rowObject of jsonArray )
        {
            let dataRow = [];

            for( let fieldName of Object.keys(rowObject) )
            {
                const cellValue = this.convertJsonValueToIValue(rowObject[fieldName]);
                const cellType = !cellValue || cellValue.getType().isEqualTo(Type.Empty)
                    ? null
                    : cellValue.getType();

                let fieldIndex = fieldsNamesToIndexMap[fieldName];
                if( fieldIndex === undefined )
                {
                    fieldsNames.push(fieldName);
                    fieldsTypes.push(cellType);

                    fieldIndex = fieldsNames.length - 1;
                    fieldsNamesToIndexMap[fieldName] = fieldIndex;
                }
                else if( !fieldsTypes[fieldIndex] )
                {
                    fieldsTypes[fieldIndex] = cellType;
                }

                dataRow[fieldIndex] = cellValue;
            }

            data.push(dataRow);
        }

        const fields = fieldsNames.map((fieldName, fieldIndex) =>
            new FrameField(fieldName, fieldsTypes[fieldIndex] || Type.String)
        );

        // the most exotic case of having actual objects with just 1 field `__items` => then we need to hint not to pluck it
        const forceNoPluck = fields.length == 1 && fields[0].name === JSON_ITEM_FIELD_NAME;
        if( forceNoPluck )
            fields.push(new FrameField(FORCE_NO_PLUCK_INDICATOR_FIELD_NAME, Type.String));

        if( isArray && data.length === 1 )
            fields.push(new FrameField(FORCE_LIST_INDICATOR_FIELD_NAME, Type.String));

        // we can't use null as value => replace those with AEmpty
        for( let dataRow of data )
        {
            for( let i = 0; i < fields.length; ++i )
            {
                if( dataRow[i] === null || dataRow[i] === undefined )
                    dataRow[i] = AEmpty.Value;
            }
        }

        return Frames.fromFieldsAndData(fields, data);
    }

    public static jsonPathToFramePath(jsonPath:string):FramePath | Error
    {
        const err:Error = new Error('Invalid json path');

        // using a hack here, because JsonPath does not support '@' as a first symbol of an identifier
        // with the below workaround a user can enter something like "RootObject[0].@attributes[0].Name[0]"
        // and it will work fine.
        jsonPath = jsonPath.replace(/\.\@/g, '.__at__');

        try
        {
            const pathComponents = jp.parse(jsonPath).filter(({expression}) => expression.type !== 'root');
            const framePath:FramePath = [];

            for( const [value1, value2] of _chunk(pathComponents, 2) as any )
            {
                if( value1.expression.type !== 'identifier' || value2.expression.type !== 'numeric_literal' )
                    return err;

                // revert the replaced @ sign
                const fieldName = value1.expression.value.replace('__at__', '@');
                const rowIndex = value2.expression.value;

                framePath.push(new FrameCellCoordinate(fieldName, rowIndex));
            }

            return framePath;
        }
        catch( e )
        {
            return err;
        }
    }
}
